import { inject, DOCUMENT } from "@angular/core";
import { Meta } from "@angular/platform-browser";
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from "@angular/router";
import { AppRouteData, AppRouteDataLink } from "../models/app-route.model";

const BASE_URL = 'https://ystm.eu';

/**
 * Router data based SEO resolver for general purpose pages of the application.
 * By applying this resolver to a component route, it will update the meta tags and canonical link of the page:
 * - Update canonical link to current url
 * - Update og:url to current url
 * - Update og:title to current title
 * - Update meta description to route.data.meta.description
 * @example
 * {
 *       path: "",
 *       loadComponent: () => import('./components/topics-overview/topics-overview.component').then(mod => mod.TopicsOverviewComponent),
 *       title: 'ADVENAGE - Topics',
 *       data: {
 *         meta: {
 *           description: 'Articles on topics related to our products, solutions and used systems'
 *         }
 *       },
 *       resolve: { seo: seoResolver },
 *   },
 */
export const seoResolver: ResolveFn<unknown> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    /**
     * DI
     */
    const meta = inject(Meta);
    const _document = inject(DOCUMENT);
    //const logService = inject(LogService);


    if (_document) {
        const canonicalLink = _document.querySelector('link[rel="canonical"]');

        /** Handle canonical link */
        if (canonicalLink) {
            /** Update canonical link */
            // logService.log('seoResolver, canonicalLink', canonicalLink);
            canonicalLink.setAttribute('href', BASE_URL + state.url);
            // meta.updateTag({ name: 'description', content: 'Fancy Article' });
        } else {
            /** Create canonical link */
            const linkElement = _document.createElement('link');
            linkElement.rel = 'canonical';
            linkElement.href = BASE_URL + state.url;
            // linkElement.setAttribute('rel', 'canonical');
            // linkElement.setAttribute('href', BASE_URL + state.url);
            _document.head.appendChild(linkElement);
        }
    } else {
        console.warn('seoResolver, no document found!');
    }
    /** handle link tags */
    const linkTags: AppRouteDataLink[] = (route.data as AppRouteData)?.linkTags ?? [];
    linkTags.forEach(linkTag => {
        const linkElement = _document.createElement('link');
        (Object.keys(linkTag) as (keyof AppRouteDataLink)[]).forEach(key => {
            linkElement.setAttribute(key, linkTag[key] as string);
        });
        _document.head.appendChild(linkElement);
    });
    /**
     * Update meta tags
     */
    /** Update og:type to 'article' */
    const ogType = meta.getTag('property="og:type"');
    // logService.log('seoResolver, ogType?.content', ogType?.content);
    if (ogType && ogType.content) {
        meta.updateTag({ property: 'og:type', content: 'article' });
    }
    /**
     * Update og:url, if defined. Set it to current url.
     */
    const ogUrl = meta.getTag('property="og:url"');
    // logService.log('seoResolver, ogUrl?.content', ogUrl?.content);
    if (ogUrl && ogUrl.content) {
        /** remove trailing slash from ogUrl.content */
        const content = ogUrl.content.replace(/\/$/, '');
        meta.updateTag({ property: 'og:url', content: content + state.url });
    }
    /**
     * Update og:title, if defined. Set it to current title.
     */
    const ogTitle = meta.getTag('property="og:title"');
    // logService.log('seoResolver, ogTitle?.content', ogTitle?.content);
    //logService.log('seoResolver, route.title', route.routeConfig?.title);
    if (ogTitle && ogTitle.content && route.routeConfig?.title && typeof route.routeConfig?.title === 'string') {
        meta.updateTag({ property: 'og:title', content: route.routeConfig?.title });
    }
    /**
     * Update meta description, if defined in route.data.meta.description
     */
    const routeData = route.data as AppRouteData;
    const description = routeData?.meta?.description;
    const ogDescription = meta.getTag('property="og:description"');
    if (description) {
        meta.updateTag({ name: 'description', content: description });
        if (ogDescription && ogDescription.content) {
            meta.updateTag({ property: 'og:description', content: description });
        }
    }

    /**
     * Update twitter:title, if defined. Set it to current title.
     */
    const twitterTitle = meta.getTag('name="twitter:title"');
    // logService.log('seoResolver, twitterTitle?.content', twitterTitle?.content);
    if (twitterTitle && twitterTitle.content && route.routeConfig?.title && typeof route.routeConfig?.title === 'string') {
        meta.updateTag({ name: 'twitter:title', content: route.routeConfig?.title });
    }
    /**
     * Update meta twitter:description, if defined in route.data.meta.description
     */
    const twitterDescription = meta.getTag('name="twitter:description"');
    if (description && twitterDescription && twitterDescription.content) {
        meta.updateTag({ name: 'twitter:description', content: description });
    }
    return null;

};

/**
 * private _updateCanonicalUrl(url?: string): void {
    const existingCanonicalUrl = this._document.querySelector(
      'link[rel="canonical"]'
    );

    if (existingCanonicalUrl) existingCanonicalUrl.remove();

    if (url) {
      const canonicalLink = this._document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = url;
      this._document.head.appendChild(canonicalLink);
    }
  }
 */